import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import DOMPurify from "dompurify";
import Loader from "../../components/common/Loader";
import Footer from "../../components/common/Footer";
import Menu from "../../components/common/Menu";
import Navbar from "../../components/common/Navbar";
import StartButton from "../../components/home/StartButton";
import FloatingCheckoutButton from "../../components/common/FloatingCheckoutButton";
import { useProduct } from "../../services/tempeFabricationCachedClient";
import type { ProductCustomizationOption } from "@tempefab-model/tempe-fabrication-client";
import { useCart } from "../../contexts/CartContext";
import CustomSelect from "../../components/common/CustomSelect";
import { usePageLoader } from "../../hooks/usePageLoader";
import "./product-detail.css";

const ProductDetail: React.FC = () => {
  usePageLoader(); // Hide loader on mount for client-side navigation

  const { id } = useParams<{ id: string }>();

  // Use cached client for 24-hour product caching
  const { data: productData, isLoading, error: queryError } = useProduct(id || "");

  const [selectedImage, setSelectedImage] = useState<string>("");
  const [selectedCustomizations, setSelectedCustomizations] = useState<Record<string, ProductCustomizationOption>>({});
  const [activeTab, setActiveTab] = useState<"specifications" | "shipping">("specifications");

  const { addItem, getItemQuantity, incrementQuantity, decrementQuantity } = useCart();

  const product = productData?.result || null;
  const loading = isLoading;
  const error = queryError ? queryError.message : (productData?.error || null);

  // Initialize UI state when product loads
  useEffect(() => {
    if (product) {
      setSelectedImage(product.keyImage || "");

      // Initialize customizations as empty (no default selection)
      setSelectedCustomizations({});

      // Set default active tab based on available data
      if (!product.productAttributes || product.productAttributes.length === 0) {
        if (product.shipping) {
          setActiveTab("shipping");
        }
      } else {
        setActiveTab("specifications");
      }
    }
  }, [product]);

  const calculateSalePrice = (price: number, discountPercent?: number) => {
    if (!discountPercent) return price;
    return price - (price * discountPercent / 100);
  };

  const handleAddToCart = () => {
    if (!product) return;

    const productId = product.id || "";
    const selectedCustomization = Object.values(selectedCustomizations)[0];
    const cartQty = getItemQuantity(productId, selectedCustomization);
    const maxInventory = product.inventory || 0;

    // If already in cart and at max inventory, do nothing
    if (cartQty > 0 && cartQty >= maxInventory) {
      return;
    }

    // If already in cart, increment by 1
    if (cartQty > 0) {
      // Generate cartItemId to increment the specific product+customization combo
      const cartItemId = selectedCustomization
        ? `${productId}__${selectedCustomization.name}`
        : productId;
      incrementQuantity(cartItemId);
      return;
    }

    // Not in cart yet, add with quantity 1
    const discount = product.sale?.discount || 0;

    addItem(
      {
        id: productId,
        name: product.name || "",
        sku: product.sku || "",
        price: product.price || 0,
        image: product.keyImage || "",
        discount: discount,
        shippingOptions: product.shippingOptions,
        customization: selectedCustomization,
      },
      1
    );
  };

  const handleDecrement = () => {
    if (!product) return;
    const productId = product.id || "";
    const selectedCustomization = Object.values(selectedCustomizations)[0];
    const cartQty = getItemQuantity(productId, selectedCustomization);

    // Allow decrementing even from 1 (which removes the item)
    if (cartQty > 0) {
      const cartItemId = selectedCustomization
        ? `${productId}__${selectedCustomization.name}`
        : productId;
      decrementQuantity(cartItemId);
    }
  };

  const handleIncrement = () => {
    if (!product) return;
    const productId = product.id || "";
    const selectedCustomization = Object.values(selectedCustomizations)[0];
    const cartQty = getItemQuantity(productId, selectedCustomization);
    const maxInventory = product.inventory || 0;

    if (cartQty > 0) {
      // Item is in cart, check if we can increment
      if (cartQty < maxInventory) {
        const cartItemId = selectedCustomization
          ? `${productId}__${selectedCustomization.name}`
          : productId;
        incrementQuantity(cartItemId);
      }
    } else {
      // Item not in cart, add it to cart with quantity 1
      if (maxInventory > 0) {
        const discount = product.sale?.discount || 0;

        addItem(
          {
            id: productId,
            name: product.name || "",
            sku: product.sku || "",
            price: product.price || 0,
            image: product.keyImage || "",
            discount: discount,
            shippingOptions: product.shippingOptions,
            customization: selectedCustomization,
          },
          1
        );
      }
    }
  };

  const renderProductContent = () => {
    if (loading) {
      return (
        <section className="tc-product-detail-section" style={{ padding: "100px 0" }}>
          <div className="container">
            <div className="text-center">
              <p>Loading product...</p>
            </div>
          </div>
        </section>
      );
    }

    if (error || !product) {
      return (
        <section className="tc-product-detail-section" style={{ padding: "100px 0" }}>
          <div className="container">
            <div className="text-center">
              <p className="text-danger">{error || "Product not found"}</p>
              <a href="/products" className="butn border rounded-pill hover-bg-black bg-orange1 text-white mt-4">
                <span>Back to Products</span>
              </a>
            </div>
          </div>
        </section>
      );
    }

    const discount = product.sale?.discount || 0;
    const originalPrice = product.price || 0;
    const displayPrice = calculateSalePrice(originalPrice, discount);
    const selectedCustomization = Object.values(selectedCustomizations)[0];
    const cartQty = getItemQuantity(product.id || "", selectedCustomization);
    const maxInventory = product.inventory || 0;

    // Determine product availability status
    const isOutOfStock = product.status === "OUT_OF_STOCK";
    const isDiscontinued = product.status === "DISCONTINUED";
    const isLimitedInventory = product.status === "LIMITED_INVENTORY";
    const isBackOrdered = product.status === "BACK_ORDERED";

    // Products cannot be added to cart if out of stock or discontinued
    const canAddToCart = !isOutOfStock && !isDiscontinued && maxInventory > 0;

    // Get delivery promise from STANDARD shipping option for back-ordered items
    const standardShippingOption = product.shippingOptions?.find(opt => opt.type === "STANDARD");
    const backOrderDeliveryPromise = standardShippingOption?.deliveryPromise;

    const allImages = [product.keyImage, ...(product.otherImages || [])].filter(Boolean);

    return (
      <section className="tc-product-detail-section" style={{ padding: "100px 0 80px" }}>
        <div className="container">
          <div className="row">
            {/* Left Column - Images */}
            <div className="col-lg-6">
              {/* Main Image */}
              <div style={{ marginBottom: "20px" }}>
                <img
                  src={selectedImage}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "500px",
                    objectFit: "contain",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    padding: "20px"
                  }}
                />
              </div>

              {/* Thumbnail Images */}
              {allImages.length > 1 && (
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {allImages.map((img, index) => img && (
                    <div
                      key={index}
                      onClick={() => setSelectedImage(img)}
                      style={{
                        width: "100px",
                        height: "100px",
                        cursor: "pointer",
                        border: selectedImage === img ? "3px solid #ff5722" : "1px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                        padding: "5px"
                      }}
                    >
                      <img
                        src={img}
                        alt={`${product.name} - ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Product Details */}
            <div className="col-lg-6">
              <div style={{ paddingLeft: "40px" }}>
                {/* Availability Banners */}
                {isOutOfStock && (
                  <div style={{
                    backgroundColor: "#f8d7da",
                    border: "1px solid #f5c6cb",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#721c24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <span style={{ color: "#721c24", fontWeight: "600", fontSize: "15px" }}>
                      Out of Stock
                    </span>
                  </div>
                )}

                {isDiscontinued && (
                  <div style={{
                    backgroundColor: "#e2e3e5",
                    border: "1px solid #d6d8db",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#383d41" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span style={{ color: "#383d41", fontWeight: "600", fontSize: "15px" }}>
                      This product has been discontinued
                    </span>
                  </div>
                )}

                {isLimitedInventory && (
                  <div style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                    <span style={{ color: "#856404", fontWeight: "600", fontSize: "15px" }}>
                      Selling fast! Limited availability
                    </span>
                  </div>
                )}

                {isBackOrdered && backOrderDeliveryPromise && (
                  <div style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span style={{ color: "#856404", fontWeight: "600", fontSize: "15px" }}>
                      Item will ship in {backOrderDeliveryPromise}
                    </span>
                  </div>
                )}


                {/* Product Name */}
                <h1 style={{
                  fontSize: "36px",
                  fontWeight: "400",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}>
                  {product.name}
                </h1>

                {/* Price */}
                <div style={{ marginBottom: "30px" }}>
                  <div style={{ marginBottom: "10px" }}>
                    {discount > 0 && (
                      <span style={{
                        textDecoration: "line-through",
                        color: "#999",
                        fontSize: "20px",
                        marginRight: "10px"
                      }}>
                        ${originalPrice.toFixed(2)}
                      </span>
                    )}
                    <span style={{
                      fontSize: "28px",
                      fontWeight: "400",
                      color: "#333"
                    }}>
                      ${displayPrice.toFixed(2)}
                    </span>
                  </div>

                  {/* Free Shipping Badge */}
                  {product.shippingOptions && product.shippingOptions.some(opt => (opt.cost || 0) === 0) && (
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: "#28a745",
                      color: "white",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="3" width="15" height="13"></rect>
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                      </svg>
                      Free Shipping
                    </div>
                  )}

                </div>

                {/* Description */}
                <div
                  style={{
                    marginBottom: "30px",
                    fontSize: "15px",
                    lineHeight: "1.8",
                    color: "#666"
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(product.description || "")
                  }}
                />

                {/* Add to Cart Section */}
                {canAddToCart && (
                  <div style={{
                    marginBottom: "30px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px"
                    }}>
                      <button
                        onClick={handleDecrement}
                        disabled={cartQty === 0}
                        className="butn border rounded-circle"
                        style={{
                          width: "36px",
                          height: "36px",
                          fontSize: "20px",
                          fontWeight: "bold",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: cartQty === 0 ? 0.5 : 1,
                          cursor: cartQty === 0 ? "not-allowed" : "pointer"
                        }}
                        aria-label="Decrease quantity"
                        title={cartQty === 0 ? "Minimum quantity is 1" : "Decrease quantity"}
                      >
                        −
                      </button>

                      <button
                        onClick={handleAddToCart}
                        disabled={cartQty >= maxInventory}
                        className="butn border rounded-pill hover-bg-black bg-orange1 text-white align-items-center"
                        style={{
                          whiteSpace: "nowrap",
                          minWidth: "140px",
                          opacity: cartQty >= maxInventory ? 0.5 : 1,
                          cursor: cartQty >= maxInventory ? "not-allowed" : "pointer"
                        }}
                        title={cartQty >= maxInventory ? "Maximum quantity reached" : "Add to cart"}
                      >
                        <span>{cartQty > 0 ? `${cartQty} Added` : "Add to Cart"}</span>
                      </button>

                      <button
                        onClick={handleIncrement}
                        disabled={cartQty >= maxInventory}
                        className="butn border rounded-circle"
                        style={{
                          width: "36px",
                          height: "36px",
                          fontSize: "20px",
                          fontWeight: "bold",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: cartQty >= maxInventory ? 0.5 : 1,
                          cursor: cartQty >= maxInventory ? "not-allowed" : "pointer"
                        }}
                        aria-label="Increase quantity"
                        title={cartQty >= maxInventory ? "Maximum quantity reached" : "Increase quantity"}
                      >
                        +
                      </button>
                    </div>
                    {cartQty > 0 && maxInventory - cartQty <= 3 && (
                      <span
                        style={{
                          fontSize: "11px",
                          color: maxInventory - cartQty === 0 ? "#dc3545" : "#ff9800",
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                        }}
                      >
                        {maxInventory - cartQty === 0 ? "Maximum quantity reached" : "Limited quantity remaining"}
                      </span>
                    )}
                  </div>
                )}

                {/* Customization Options */}
                {product.customization && product.customization.length > 0 && (
                  <div style={{ marginBottom: "30px" }}>
                    {product.customization.map((custom, index) => (
                      <div key={index} style={{ marginBottom: "20px" }}>
                        <CustomSelect
                          label={custom.key || ""}
                          options={[
                            "None",
                            ...(custom.possibleValues || []).map(opt => {
                              const additionalCost = opt.additionalCost || 0;
                              const costDisplay = additionalCost > 0 ? ` (+$${additionalCost.toFixed(2)})` : '';
                              return `${opt.name || ''}${costDisplay}`;
                            })
                          ]}
                          value={(() => {
                            const selected = selectedCustomizations[custom.key || ""];
                            if (selected) {
                              const additionalCost = selected.additionalCost || 0;
                              const costDisplay = additionalCost > 0 ? ` (+$${additionalCost.toFixed(2)})` : '';
                              return `${selected.name || ''}${costDisplay}`;
                            }
                            return "None";
                          })()}
                          onChange={(value) => {
                            // If "None" is selected, remove the customization
                            if (value === "None") {
                              const updatedCustomizations = { ...selectedCustomizations };
                              delete updatedCustomizations[custom.key || ""];
                              setSelectedCustomizations(updatedCustomizations);
                              return;
                            }

                            // Extract the name from the display value (remove cost suffix)
                            const optionName = value.split(' (+$')[0];
                            // Find the full ProductCustomizationOption object by name
                            const selectedOption = custom.possibleValues?.find(opt => opt.name === optionName);
                            if (selectedOption) {
                              setSelectedCustomizations({
                                ...selectedCustomizations,
                                [custom.key || ""]: selectedOption
                              });
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Features */}
                {product.features && product.features.length > 0 && (
                  <div style={{ marginBottom: "30px" }}>
                    <ul style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0
                    }}>
                      {product.features.map((feature, index) => (
                        <li key={index} style={{
                          marginBottom: "10px",
                          fontSize: "15px",
                          color: "#666",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px"
                        }}>
                          <span style={{ color: "#4a90e2", fontSize: "18px" }}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Shipping & Specifications Tabs */}
          {((product.productAttributes && product.productAttributes.length > 0) || product.shipping) && (
            <div className="row" style={{ marginTop: "60px" }}>
              <div className="col-12">
                <div className="filter mb-60">
                  <div className="links">
                    {product.productAttributes && product.productAttributes.length > 0 && (
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("specifications");
                        }}
                        className={`filter-btn ${activeTab === "specifications" ? "active" : ""}`}
                        href="#0"
                        style={{
                          fontSize: "18px",
                          lineHeight: "24px",
                          color: "#666",
                          backgroundColor: "transparent",
                          padding: "0 0 8px 0",
                          marginInlineEnd: "50px",
                          textTransform: "capitalize",
                          cursor: "pointer",
                          borderBottom: activeTab === "specifications" ? "3px solid var(--color-orange1)" : "3px solid transparent",
                          transition: "border-bottom 0.3s ease"
                        }}
                      >
                        Product Specifications
                      </a>
                    )}
                    {product.shipping && (
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("shipping");
                        }}
                        className={`filter-btn ${activeTab === "shipping" ? "active" : ""}`}
                        href="#0"
                        style={{
                          fontSize: "18px",
                          lineHeight: "24px",
                          color: "#666",
                          backgroundColor: "transparent",
                          padding: "0 0 8px 0",
                          marginInlineEnd: "50px",
                          textTransform: "capitalize",
                          cursor: "pointer",
                          borderBottom: activeTab === "shipping" ? "3px solid var(--color-orange1)" : "3px solid transparent",
                          transition: "border-bottom 0.3s ease"
                        }}
                      >
                        Shipping Information
                      </a>
                    )}
                  </div>
                </div>

              {activeTab === "specifications" && product.productAttributes && product.productAttributes.length > 0 && (
                <div style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "24px",
                  marginBottom: "30px"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {product.productAttributes.map((attr, index) => (
                        <tr key={index} style={{
                          borderBottom: index < product.productAttributes!.length - 1 ? "1px solid #f0f0f0" : "none"
                        }}>
                          <td style={{
                            fontSize: "15px",
                            color: "#333",
                            fontWeight: "600",
                            padding: "12px 20px 12px 0",
                            verticalAlign: "top",
                            width: "40%"
                          }}>
                            {attr.attribute}:
                          </td>
                          <td style={{
                            fontSize: "15px",
                            color: "#666",
                            padding: "12px 0",
                            verticalAlign: "top"
                          }}>
                            {attr.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "shipping" && product.shipping && (
                <div style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "24px",
                  marginBottom: "30px"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      <tr style={{
                        borderBottom: product.shipping.weight ? "1px solid #f0f0f0" : "none"
                      }}>
                        <td style={{
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "600",
                          padding: "12px 20px 12px 0",
                          verticalAlign: "top",
                          width: "40%"
                        }}>
                          Dimensions (W, H, D):
                        </td>
                        <td style={{
                          fontSize: "15px",
                          color: "#666",
                          padding: "12px 0",
                          verticalAlign: "top"
                        }}>
                          {product.shipping.width}"
                          {product.shipping.height && `, ${product.shipping.height}"`}
                          {product.shipping.depth && `, ${product.shipping.depth}"`}
                          {product.shipping.dimensionUnit && ` ${product.shipping.dimensionUnit}`}
                        </td>
                      </tr>

                      {product.shipping.weight && (
                        <tr>
                          <td style={{
                            fontSize: "15px",
                            color: "#333",
                            fontWeight: "600",
                            padding: "12px 20px 12px 0",
                            verticalAlign: "top",
                            width: "40%"
                          }}>
                            Weight:
                          </td>
                          <td style={{
                            fontSize: "15px",
                            color: "#666",
                            padding: "12px 0",
                            verticalAlign: "top"
                          }}>
                            {product.shipping.weight} {product.shipping.weightUnit || "lb"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <>
      <Helmet>
        <title>{product?.name || "Product Details"} | Tempe Fabrication</title>
        <meta name="description" content={product?.description || "View product details"} />
        <link rel="stylesheet" type="text/css" href="/home/assets/css/home_1_style.css" />
        <link rel="stylesheet" type="text/css" href="/innerpages/assets/css/innerpages.css" />
        <script src="/common/assets/js/common_js.js"></script>
        <script src="/innerpages/assets/js/innerpages.js"></script>
      </Helmet>
      <div className="inner-pages-style1 products-pg-style1">
        <Loader />
        <Menu />
        <div className="smooth-scroll-content" id="scrollsmoother-container">
          <Navbar currentPage="products" />
          <header className="tc-inner-header-style1" style={{ visibility: "hidden", height: "0", overflow: "hidden", margin: "0", padding: "0" }}>
            <div className="container">
              <div className="info col-lg-8">
                <h2 className="js-title">Product Details</h2>
              </div>
            </div>
          </header>
          {renderProductContent()}
          <Footer />
        </div>
        <StartButton />
        <FloatingCheckoutButton />
      </div>
    </>
  );
};

export default ProductDetail;
